import { Suspense, useDeferredValue, useEffect, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import { Redirect, Switch, useRouteMatch } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import { fetchAccountAddress } from 'api/account';
import { ContactHouseholdFormDrawer } from 'assets/js/pages/contacts/ContactHouseholdFormDrawer';
import { LetterCompose } from 'assets/js/pages/engage/letters/LetterCompose';
import { LetterConfirmation } from 'assets/js/pages/engage/letters/LetterConfirmation';
import { LetterConfirmationEasySend } from 'assets/js/pages/engage/letters/LetterConfirmationEasySend';
import { LetterDetailsForm } from 'assets/js/pages/engage/letters/LetterDetailsForm';
import { LetterEnvelopePreview } from 'assets/js/pages/engage/letters/LetterEnvelopePreview';
import { LetterFilters } from 'assets/js/pages/engage/letters/LetterFilters';
import { LetterFormNavigation } from 'assets/js/pages/engage/letters/LetterFormNavigation';
import { PersonalizedLetters } from 'assets/js/pages/engage/letters/PersonalizedLetters';
import { transformLetterSettingsToForm } from 'assets/js/pages/engage/letters/transform';
import { LetterDraftFormValues } from 'assets/js/pages/engage/letters/types';
import { useCurrentLetter } from 'assets/js/pages/engage/letters/useCurrentLetter';
import { useDraftAutoSave } from 'assets/js/pages/engage/letters/useDraftAutoSave';
import { usePersonalizedLettersStore } from 'assets/js/pages/engage/letters/usePersonalizedLettersStore';
import { AddressFormDrawer } from 'components/address-form/AddressFormDrawer';
import * as Dialog from 'components/dialog';
import { DrawerRoute } from 'components/drawer-route';
import * as Form from 'components/form';
import { Route } from 'components/router/Route';
import { useFiltersCache } from 'contexts/useFiltersCache';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { isoDateFromServer } from 'helpers/dates';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { isEqual } from 'lodash';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';

const AUTOSAVE_DEBOUNCE = 500;

const LetterDraftRouter = () => {
    const match = useRouteMatch();
    const { accountId } = useCurrentAccount();
    const clearFilterByKey = useFiltersCache(state => state.clearFilterByKey);
    const { data: letter } = useCurrentLetter();
    const isDraft = letter?.status === 'draft';
    const isEasySendLetter = letter?.settings.send_to_print_and_mail_service === true;
    const { setPersonalizedLetterSearch } = usePersonalizedLettersStore();
    const senderId = useDeferredValue(letter.sender_id);
    const { data: currentReturnAddress } = useSuspenseQuery({
        queryKey: accountQueryKeys.addressById(accountId, Number(senderId)),
        queryFn: () =>
            conditionallyFetchOrNull(
                () => fetchAccountAddress(accountId, senderId ?? 0),
                senderId !== null,
            ),
    });
    const returnAddressDefault = currentReturnAddress
        ? currentReturnAddress.id.toString()
        : undefined;
    const methods = Form.useGivebutterForm<LetterDraftFormValues>({
        defaultValues: {
            name: letter.internal_name ?? '',
            contactFilters: letter.recipient_filters ?? [],
            generateFor: letter.settings.generate_letter_for ?? 'all',
            includeContactsWithNoAddress: !letter.settings.is_address_required,
            excludeContactsMissingMergeFields:
                letter.settings.exclude_contacts_missing_merge_fields ?? true,
            processingSpeed: letter.sending_at ? 'scheduled' : 'immediate',
            shippingDate: letter.sending_at
                ? isoDateFromServer(letter.sending_at)
                : DateTime.now().plus({ days: 1 }),
            hasReviewedMailings: false,
            returnAddress: returnAddressDefault,
            letterSettings: transformLetterSettingsToForm(letter),
        },
    });
    const { mutate: autoSave } = useDraftAutoSave(methods);
    const { control, setValue } = methods;
    const values = useWatch({ control });
    const debouncedValues = useDebounce(values, AUTOSAVE_DEBOUNCE);
    const oldValues = useRef(debouncedValues);

    useEffect(() => {
        const hasDiff = !isEqual(oldValues.current, debouncedValues);
        if (hasDiff && isDraft) {
            autoSave();
            oldValues.current = debouncedValues;
        }
    }, [isDraft, debouncedValues, autoSave]);

    useEffect(() => {
        return () => {
            clearFilterByKey('engage');
        };
    }, [clearFilterByKey]);

    useEffect(() => {
        return () => {
            setPersonalizedLetterSearch('');
        };
    }, [setPersonalizedLetterSearch]);

    if (!letter) {
        return <Redirect to={`/accounts/${accountId}/engage/letters`} />;
    }

    if (!isDraft) {
        return <Redirect to={`/accounts/${accountId}/engage/letters/${letter.id}`} />;
    }

    return (
        <Dialog.Root open={true} modal={false}>
            <Dialog.Portal>
                <Dialog.Content variant="fullscreen">
                    <Dialog.Title className="sr-only">Letter draft</Dialog.Title>
                    <Form.GivebutterFormProvider {...methods}>
                        <form className="contents">
                            <div className={`bg-gray-25 flex h-full flex-col gap-6 pb-6`}>
                                <LetterFormNavigation />
                                <Suspense>
                                    <Switch>
                                        <Route path={`${match.path}/details`}>
                                            {({ match }) => (
                                                <>
                                                    <DrawerRoute
                                                        path={`${match?.path}/addresses/new`}
                                                    >
                                                        <AddressFormDrawer
                                                            isMailingAddress={true}
                                                            onSuccess={newAddress =>
                                                                setValue(
                                                                    'returnAddress',
                                                                    newAddress.id.toString(),
                                                                    { shouldDirty: true },
                                                                )
                                                            }
                                                        />
                                                    </DrawerRoute>
                                                    <Switch>
                                                        <Route
                                                            path={match?.path}
                                                            component={LetterDetailsForm}
                                                        />
                                                    </Switch>
                                                </>
                                            )}
                                        </Route>
                                        <Route
                                            exact
                                            component={LetterCompose}
                                            path={`${match.path}/compose`}
                                        />
                                        <Route
                                            component={LetterFiltersRouter}
                                            path={`${match.path}/filters`}
                                        />
                                        <Route
                                            exact
                                            component={LetterEnvelopePreview}
                                            path={`${match.path}/envelope`}
                                        />
                                        <Route
                                            path={`${match.path}/personalize`}
                                            component={PersonalizedLettersRouter}
                                        />
                                        <Route
                                            exact
                                            component={
                                                isEasySendLetter
                                                    ? LetterConfirmationEasySend
                                                    : LetterConfirmation
                                            }
                                            path={`${match.path}/confirm`}
                                        />
                                    </Switch>
                                </Suspense>
                            </div>
                        </form>
                    </Form.GivebutterFormProvider>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const PersonalizedLettersRouter = () => {
    const match = useRouteMatch();
    return (
        <>
            <DrawerRoute
                path={[`${match.path}/contacts/:contact_id/household/:household_id/update`]}
                component={ContactHouseholdFormDrawer}
            />
            <Switch>
                <Route component={PersonalizedLetters} path={`${match.path}`} />
            </Switch>
        </>
    );
};

const LetterFiltersRouter = () => {
    const match = useRouteMatch();
    return (
        <>
            <DrawerRoute
                path={[`${match.path}/contacts/:contact_id/household/:household_id/update`]}
                component={ContactHouseholdFormDrawer}
            />
            <Switch>
                <Route component={LetterFilters} path={`${match.path}`} />
            </Switch>
        </>
    );
};

export { LetterDraftRouter };
export type { LetterDraftFormValues };
